import React, { useEffect, useRef, useState } from 'react';
import { useModal } from 'hooks/3cat/useModal';
import { useFocus } from 'hooks/3cat/useFocus';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import styles from './modal.module.scss';

const Modal = (props) => {
    const { openModal, modalContent, optionsModal, removeModal } = useModal();
    const { getFocusableElements } = useFocus();
    const [topContentScroll, setTopContentScroll] = useState(0);
    const [isVisibleModal, setIsVisibleModal] = useState(false);
    const { keyDownCustom } = optionsModal;
    const contentRef = useRef(null);
    const modalRef = useRef(null);
    const router = useRouter();
    const styleModal = clsx({
        [styles.modal]: true,
        [styles.visible]: isVisibleModal,
        [styles[optionsModal?.mode]]: !!optionsModal?.mode,
        [styles[optionsModal?.style]]: !!optionsModal?.style,
        [optionsModal.className]: !!optionsModal?.className
    });

    const handleClickOutside = (e) => {
        if (optionsModal.onBeforeClickOutside) {
            if (modalRef.current?.contains(e.target) && contentRef.current && !contentRef.current.contains(e.target)) {
                optionsModal.onBeforeClickOutside(e);
            }
        }

        if (modalRef.current?.contains(e.target) && contentRef.current && !contentRef.current.contains(e.target)) {
            if (optionsModal?.onClickClose) {
                optionsModal.onClickClose();
            } else {
                removeModal();
            }
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClickClose();
        }

        if (e.key === 'Tab' && !keyDownCustom) {
            const modalEl = modalRef.current;
            if (!modalEl) return;

            const focusables = getFocusableElements(modalEl);
            if (focusables.length === 0) {
                e.preventDefault();
                modalEl.focus();
                return;
            }

            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;

            const isActiveInsideModal = modalEl.contains(active);

            if (!isActiveInsideModal) {
                e.preventDefault();
                first.focus();
                return;
            }

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        }
    };

    useEffect(() => {
        const modalRefCurrent = modalRef.current;
        if (modalContent) {
            if (optionsModal.mode !== 'full-screen') setTopContentScroll(window.scrollY);
            if (optionsModal.closeOnClickOutside) {
                modalRefCurrent.addEventListener('mousedown', handleClickOutside);
            } else {
                modalRefCurrent.removeEventListener('mousedown', handleClickOutside);
            }
        }

        return () => {
            if (modalRefCurrent) modalRefCurrent.removeEventListener('mousedown', handleClickOutside);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalContent]);

    useEffect(() => {
        if (openModal) {
            //Assegurem que no es canvia l'estat fins després del render
            setTimeout(() => {
                setIsVisibleModal(true);
            }, 0);
            window.addEventListener('keydown', handleKeyDown);
        } else {
            window.removeEventListener('keydown', handleKeyDown);
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal]);

    const onClickClose = () => {
        if (optionsModal?.onClickClose) {
            optionsModal.onClickClose();
        } else {
            setIsVisibleModal(false);
            setTimeout(() => {
                removeModal();
            }, 300);
        }
    };

    useEffect(() => {
        const handleRouteChange = () => {
            onClickClose();
        };

        router.events.on('routeChangeStart', handleRouteChange);

        return () => {
            router.events.off('routeChangeStart', handleRouteChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router]);

    return openModal ? (
        <div
            className={styleModal}
            id='container-modal'
            role='dialog'
            aria-modal='true'
            aria-label={optionsModal?.title}
            style={{ top: topContentScroll }}
            ref={modalRef}
        >
            <div className={styles.inner} id='content-modal' ref={contentRef}>
                {React.isValidElement(modalContent)
                    ? React.cloneElement(modalContent, { onClickClose, isVisibleModal })
                    : modalContent}
            </div>
        </div>
    ) : (
        <></>
    );
};

export default Modal;
